import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  Select, 
  Card, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Tag,
  Modal,
  Form,
  DatePicker,
  message
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  FileTextOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from '../hooks/useTranslation';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

interface Claim {
  id: string;
  reference: string;
  patientName: string;
  doctorName: string;
  clinicName: string;
  claimDate: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected' | 'processing';
  type: 'consultation' | 'medication' | 'procedure' | 'follow_up';
  description: string;
  submittedBy: string;
  submittedAt: string;
}

const Claims: React.FC = () => {
  const { t } = useTranslation();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Mock data - replace with actual API call
  useEffect(() => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const mockClaims: Claim[] = [
        {
          id: '1',
          reference: 'CLM-2024-001',
          patientName: 'John Doe',
          doctorName: 'Dr. Sarah Johnson',
          clinicName: 'Central Medical Clinic',
          claimDate: '2024-01-15',
          amount: 500,
          status: 'pending',
          type: 'consultation',
          description: 'General consultation fee',
          submittedBy: 'Dr. Sarah Johnson',
          submittedAt: '2024-01-15T10:30:00Z'
        },
        {
          id: '2',
          reference: 'CLM-2024-002',
          patientName: 'Jane Smith',
          doctorName: 'Dr. Michael Chen',
          clinicName: 'Eastern Medical Center',
          claimDate: '2024-01-14',
          amount: 1200,
          status: 'approved',
          type: 'procedure',
          description: 'Minor surgical procedure',
          submittedBy: 'Dr. Michael Chen',
          submittedAt: '2024-01-14T14:20:00Z'
        },
        {
          id: '3',
          reference: 'CLM-2024-003',
          patientName: 'Robert Wilson',
          doctorName: 'Dr. Emily Davis',
          clinicName: 'Western Health Clinic',
          claimDate: '2024-01-13',
          amount: 300,
          status: 'rejected',
          type: 'medication',
          description: 'Prescription medication',
          submittedBy: 'Dr. Emily Davis',
          submittedAt: '2024-01-13T09:15:00Z'
        }
      ];
      setClaims(mockClaims);
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'orange';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'processing':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockCircleOutlined />;
      case 'approved':
        return <CheckCircleOutlined />;
      case 'rejected':
        return <CloseCircleOutlined />;
      case 'processing':
        return <ClockCircleOutlined />;
      default:
        return null;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'consultation':
        return t('claims.types.consultation');
      case 'medication':
        return t('claims.types.medication');
      case 'procedure':
        return t('claims.types.procedure');
      case 'follow_up':
        return t('claims.types.follow_up');
      default:
        return type;
    }
  };

  const filteredClaims = claims.filter(claim => {
    const matchesSearch = claim.reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         claim.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         claim.doctorName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;
    const matchesType = typeFilter === 'all' || claim.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleAddClaim = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      // Handle form submission
      console.log('Form values:', values);
      message.success(t('claims.claimCreated'));
      setIsModalVisible(false);
      form.resetFields();
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns = [
    {
      title: t('claims.reference'),
      dataIndex: 'reference',
      key: 'reference',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: t('claims.patientName'),
      dataIndex: 'patientName',
      key: 'patientName'
    },
    {
      title: t('claims.doctorName'),
      dataIndex: 'doctorName',
      key: 'doctorName'
    },
    {
      title: t('claims.clinicName'),
      dataIndex: 'clinicName',
      key: 'clinicName'
    },
    {
      title: t('claims.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag>{getTypeLabel(type)}</Tag>
    },
    {
      title: t('claims.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          HK$ {amount.toLocaleString()}
        </Text>
      )
    },
    {
      title: t('claims.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {t(`claims.statuses.${status}`)}
        </Tag>
      )
    },
    {
      title: t('claims.claimDate'),
      dataIndex: 'claimDate',
      key: 'claimDate'
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (text: string, record: Claim) => (
        <Space size="small">
          <Button type="link" size="small">{t('common.view')}</Button>
          <Button type="link" size="small">{t('common.edit')}</Button>
        </Space>
      )
    }
  ];

  const totalClaims = claims.length;
  const pendingClaims = claims.filter(c => c.status === 'pending').length;
  const approvedClaims = claims.filter(c => c.status === 'approved').length;
  const totalAmount = claims.reduce((sum, claim) => sum + claim.amount, 0);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <FileTextOutlined style={{ marginRight: 8 }} />
          {t('claims.title')}
        </Title>
        <Text type="secondary">{t('claims.subtitle')}</Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('claims.totalClaims')}
              value={totalClaims}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('claims.pendingClaims')}
              value={pendingClaims}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('claims.approvedClaims')}
              value={approvedClaims}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('claims.totalAmount')}
              value={totalAmount}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="HK$"
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Search
              placeholder={t('claims.searchClaims')}
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('claims.filterByStatus')}
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('claims.allStatus')}</Option>
              <Option value="pending">{t('claims.statuses.pending')}</Option>
              <Option value="approved">{t('claims.statuses.approved')}</Option>
              <Option value="rejected">{t('claims.statuses.rejected')}</Option>
              <Option value="processing">{t('claims.statuses.processing')}</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('claims.filterByType')}
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('claims.allTypes')}</Option>
              <Option value="consultation">{t('claims.types.consultation')}</Option>
              <Option value="medication">{t('claims.types.medication')}</Option>
              <Option value="procedure">{t('claims.types.procedure')}</Option>
              <Option value="follow_up">{t('claims.types.follow_up')}</Option>
            </Select>
          </Col>
          <Col span={8} style={{ textAlign: 'right' }}>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddClaim}
            >
              {t('claims.addClaim')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Claims Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredClaims}
          loading={loading}
          rowKey="id"
          pagination={{
            total: filteredClaims.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} claims`
          }}
        />
      </Card>

      {/* Add Claim Modal */}
      <Modal
        title={t('claims.addClaim')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="patientName"
                label={t('claims.patientName')}
                rules={[{ required: true, message: t('claims.pleaseInputPatientName') }]}
              >
                <Input placeholder={t('claims.patientNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="doctorName"
                label={t('claims.doctorName')}
                rules={[{ required: true, message: t('claims.pleaseInputDoctorName') }]}
              >
                <Input placeholder={t('claims.doctorNamePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="clinicName"
                label={t('claims.clinicName')}
                rules={[{ required: true, message: t('claims.pleaseInputClinicName') }]}
              >
                <Input placeholder={t('claims.clinicNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="claimDate"
                label={t('claims.claimDate')}
                rules={[{ required: true, message: t('claims.pleaseSelectClaimDate') }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label={t('claims.type')}
                rules={[{ required: true, message: t('claims.pleaseSelectClaimType') }]}
              >
                <Select placeholder={t('claims.filterByType')}>
                  <Option value="consultation">{t('claims.types.consultation')}</Option>
                  <Option value="medication">{t('claims.types.medication')}</Option>
                  <Option value="procedure">{t('claims.types.procedure')}</Option>
                  <Option value="follow_up">{t('claims.types.follow_up')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label={t('claims.amount')}
                rules={[{ required: true, message: t('claims.pleaseInputAmount') }]}
              >
                <Input placeholder={t('claims.amountPlaceholder')} type="number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="description"
            label={t('claims.description')}
            rules={[{ required: true, message: t('claims.pleaseInputDescription') }]}
          >
            <Input.TextArea rows={3} placeholder={t('claims.descriptionPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Claims; 